import {useState} from 'react';
import styled from 'styled-components';
import {LoadingBar} from '@/components/LoadingBar';
import {Typography} from '@/components/Typography';
import {
  AnalysisItem,
  BasicAnalysisResult,
  KodataCategoryGroup,
} from '@/map/@types';
import {
  ColumnFlexBox,
  Flexbox,
  FlexFullGrow,
  PaddingBox,
} from '@/styled-components';
import {palette} from '@/styles';
import {humanizeNumber} from '@/utils';
import {ErrorBox} from '../ErrorBox';
import {NoData} from '../NoData';
import {ReportContainer, SectionLabel} from '../StyledComponents';
import {CategorySalesItem} from './CategorySalesItem';
import {CategorySelectModal} from './CategorySelectModal';
import SearchImg from './search-category.svg';
import {useAtomValue} from 'jotai';
import {mapAgentTypeAtom} from '@/map/atom';
import {useGA4WithParams} from '@/hooks/tracking';
import {AreaOverlayType} from '@/map/enums';

interface IProps {
  item: AnalysisItem;
  data?: BasicAnalysisResult['hallSales'];
  isPending?: boolean;
  error?: string;
}

export const HallSalesReport = ({item, data, isPending, error}: IProps) => {
  const [openCategoryModal, setOpenCategoryModal] = useState<boolean>();
  const [selectedCategory, setSelctedCategory] = useState<
    KodataCategoryGroup[]
  >([]);
  const mapAgent = useAtomValue(mapAgentTypeAtom);

  const tracking = useGA4WithParams();

  const onSelectedCategory = (selected: KodataCategoryGroup[]) => {
    setSelctedCategory(selected);
    setOpenCategoryModal(false);
    tracking('hallSales_circle_mobile', {
      radius: item?.[AreaOverlayType.CIRCLE]?.radius,
      businessCategory: selected.map((i) => i.categoryDetail),
      agent: mapAgent,
    });
  };

  if (isPending || error || !data) {
    return (
      <ReportContainer>
        {isPending ? (
          <LoadingBar color={palette.textSecondary} />
        ) : error ? (
          <ErrorBox />
        ) : (
          <NoData text="분석 지역 내 홀 매출 정보가 없습니다." />
        )}
      </ReportContainer>
    );
  }

  return (
    <ReportContainer>
      <PaddingBox bottom={16}>
        <Flexbox>
          <div>
            <SectionLabel color={palette.gray800}>총 매출</SectionLabel>
            <PaddingBox top={8}>
              <Typography variant="bold24">
                {data > 0 ? `${humanizeNumber(data, true)} 원` : '정보없음'}
              </Typography>
            </PaddingBox>
          </div>
          <FlexFullGrow />
          <Button onClick={() => setOpenCategoryModal(true)}>업종 선택</Button>
        </Flexbox>
      </PaddingBox>
      {!selectedCategory.length ? (
        <NoCategoryBox>
          <Typography variant="medium14" color={palette.gray600}>
            조회하고 싶은 업종을 선택해주세요.
          </Typography>
          <img src={SearchImg} alt="업종선택" />
        </NoCategoryBox>
      ) : (
        <PaddingBox top={12}>
          <ColumnFlexBox gap={16}>
            {selectedCategory.map((i) => (
              <CategorySalesItem
                category={i}
                item={item}
                key={i.categoryDetail}
              />
            ))}
          </ColumnFlexBox>
        </PaddingBox>
      )}
      {openCategoryModal && (
        <CategorySelectModal
          defaultOptions={selectedCategory}
          onClose={() => setOpenCategoryModal(false)}
          onComplete={onSelectedCategory}
        />
      )}
    </ReportContainer>
  );
};

const NoCategoryBox = styled.div`
  padding: 20px 0;
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 8px;
`;

const Button = styled.button`
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${palette.teal100};
  color: ${palette.teal200};
  font-size: 14px;
  line-height: 17px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
`;
